import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/lib/apiClient'
import { accessToken } from '@/lib/staffSession'

import type {
  BraceletOut,
  CheckinSummary,
  DoorEntry,
  DoorCollectMethod,
  DoorCollectResult,
  DoorPaymentMethod,
  DoorTally,
  DoorTicket,
  DoorTill,
  FreeTable,
  ScanResponse,
} from './types'

/**
 * Surface d'exploitation du soir J.
 *
 * Le scan renvoie volontairement des statuts HTTP distincts (200 / 409 / 402 /
 * 404) : la tablette peut réagir instantanément. Comme ces réponses portent une
 * donnée métier et non une erreur, on les accepte explicitement.
 */
const SCAN_STATUSES = [402, 404, 409]

export function useScan(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { token: string; device_label?: string }) =>
      apiClient.post<ScanResponse>('/ops/checkin/scan', payload, {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
        acceptStatuses: SCAN_STATUSES,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['checkin-summary', eventId] })
    },
  })
}

/**
 * Faire entrer **une** personne du groupe.
 *
 * Le scan a ouvert la liste ; c'est ce geste-ci qui ouvre la porte, et il se
 * répète nom par nom. Les mêmes statuts que le scan sont acceptés : « déjà
 * entré » et « impayé » sont des réponses à lire, pas des pannes.
 */
export function useCheckInParticipant(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ attendeeId }: { attendeeId: string }) =>
      apiClient.post<ScanResponse>(
        `/ops/checkin/${attendeeId}/check-in`,
        { device_label: navigator.userAgent.slice(0, 60) },
        {
          token: await accessToken(),
          ...(eventId ? { eventId } : {}),
          acceptStatuses: SCAN_STATUSES,
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['checkin-summary', eventId] })
    },
  })
}

export function useUndoCheckin(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ attendeeId, reason }: { attendeeId: string; reason: string }) =>
      apiClient.post<ScanResponse>(
        `/ops/checkin/${attendeeId}/undo`,
        { reason },
        { token: await accessToken(), ...(eventId ? { eventId } : {}) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['checkin-summary', eventId] })
    },
  })
}

export function useDistributeBracelet(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      attendeeId,
      code,
      braceletType,
    }: {
      attendeeId: string
      code?: string
      braceletType?: string
    }) =>
      apiClient.post<BraceletOut>(
        `/ops/checkin/${attendeeId}/bracelet`,
        { code: code ?? null, bracelet_type: braceletType ?? null },
        { token: await accessToken(), ...(eventId ? { eventId } : {}) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['checkin-summary', eventId] })
    },
  })
}

export function useCheckinSummary(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['checkin-summary', eventId],
    queryFn: async () =>
      apiClient.get<CheckinSummary>('/ops/checkin/summary', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: 30_000,
  })
}


/**
 * Les tables encore libres pour une réservation.
 *
 * Interrogées à chaque ouverture du sélecteur, jamais mises en cache long :
 * une autre entrée peut avoir donné la table entre-temps, et proposer une
 * place déjà prise fait perdre plus de temps que la requête n'en coûte.
 */
export function useFreeTables(reservationId: string | null, eventId: string | undefined) {
  return useQuery({
    queryKey: ['free-tables', reservationId, eventId],
    queryFn: async () =>
      apiClient.get<FreeTable[]>(`/ops/checkin/reservations/${reservationId}/free-tables`, {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(reservationId) && Boolean(eventId),
    staleTime: 0,
  })
}

export function useAssignTable(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: {
      reservationId: string
      physical_table_id: string | null
    }) =>
      apiClient.post<{ reservation_id: string; table_number: string | null; party_size: number }>(
        `/ops/checkin/reservations/${variables.reservationId}/table`,
        { physical_table_id: variables.physical_table_id },
        { token: await accessToken(), ...(eventId ? { eventId } : {}) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['free-tables'] })
      // Le plan de salle de la régie doit refléter le placement aussitôt.
      void queryClient.invalidateQueries({ queryKey: ['floor-plan'] })
    },
  })
}


// ── Les entrees vendues a la porte ─────────────────────────────────

/** Les formules proposees a l'entree : solo, couple, ce que l'equipe a cree. */
export function useDoorTickets(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['door-tickets', eventId],
    queryFn: async () =>
      apiClient.get<DoorTicket[]>('/ops/checkin/door/tickets', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
  })
}

/** Le compte de la porte, rafraichi comme le reste de l'ecran. */
export function useDoorTally(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['door-tally', eventId],
    queryFn: async () =>
      apiClient.get<DoorTally>('/ops/checkin/door/tally', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: 30_000,
  })
}

export function useRecordDoorEntry(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      ticket_type_id: string
      quantity: number
      payment_method: DoorPaymentMethod
    }) =>
      apiClient.post<DoorEntry>('/ops/checkin/door/entries', payload, {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['door-tally', eventId] })
      // Les entrées de la porte comptent dans « Entrées », en tête d'écran.
      void queryClient.invalidateQueries({ queryKey: ['checkin-summary', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-session', eventId] })
    },
  })
}

export function useDeleteDoorEntry(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { id: string }) =>
      apiClient.delete<{ message: string }>(
        `/ops/checkin/door/entries/${payload.id}`,
        {
          token: await accessToken(),
          ...(eventId ? { eventId } : {}),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['door-tally', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['checkin-summary', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-session', eventId] })
    },
  })
}


// ── Encaisser à la porte une commande non réglée ───────────────────

/**
 * La caisse que je tiens, ou `null`.
 *
 * Encaisser à l'entrée demande la caisse de l'entrée : sans elle, l'argent
 * d'une commande en ligne réglée en liquide n'entrerait dans aucun tiroir.
 * Un 403 est une réponse — ce rôle ne tient pas de caisse —, pas une panne.
 */
export function useDoorTill(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['pos-session', eventId],
    queryFn: async () =>
      apiClient.get<DoorTill | null>('/ops/pos/session', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
    retry: false,
  })
}

export function useCollectAtDoor(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      orderId: string
      method: DoorCollectMethod
      received_minor: number | null
    }) =>
      apiClient.post<DoorCollectResult>(
        `/ops/checkin/orders/${payload.orderId}/collect`,
        {
          method: payload.method,
          ...(payload.received_minor !== null ? { received_minor: payload.received_minor } : {}),
        },
        { token: await accessToken(), ...(eventId ? { eventId } : {}) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['checkin-summary', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['pos-session', eventId] })
    },
  })
}
